import { c as _c } from "react/compiler-runtime";
/**
 * @file useBaseFilters.ts
 * @description
 * -----------------------------------------------------
 * Hook genérico para manejar filtros en la aplicación
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-27
 * @modified 2026-03-26
 * @author DLTCode
 * @author Daniel Sentamans
 * @modified Sergio Manjón
 * @copyright © DLTCode
 * @license Private
 */

import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
import { defaultAnomaliesFilters, defaultCompaniesFilters, defaultDashboardFilters, defaultFinishRegistrationFilters, defaultMapFilters, defaultOneCompanyFilters, defaultSignalHistoryFilters, defaultSignalRecordingsFilters, defaultUsersFilters, defaultVerificationFilters, defaultUsersActivityFilters, defaultReportsFilters } from "@/constants/filters";
import { BaseFilters } from "@/enums/baseFilters.enum";
type FilterValue = string | number | boolean | string[] | number[] | boolean[] | null | undefined;

// Mapeo de secciones para recoger sus filtros por defecto
const sectionFilters = {
  [BaseFilters.USERS]: defaultUsersFilters,
  [BaseFilters.USERS_ACTIVITY]: defaultUsersActivityFilters,
  [BaseFilters.USERS_VERIFICATION]: defaultVerificationFilters,
  [BaseFilters.COMPANIES]: defaultCompaniesFilters,
  [BaseFilters.ONECOMPANY]: defaultOneCompanyFilters,
  [BaseFilters.MAP]: defaultMapFilters,
  [BaseFilters.ANOMALIES]: defaultAnomaliesFilters,
  [BaseFilters.DASHBOARD]: defaultDashboardFilters,
  [BaseFilters.SIGNAL_HISTORY]: defaultSignalHistoryFilters,
  [BaseFilters.REPORTS]: defaultReportsFilters,
  [BaseFilters.SIGNAL_RECORDINGS]: defaultSignalRecordingsFilters,
  [BaseFilters.FINISH_REGISTRATION]: defaultFinishRegistrationFilters
} as const satisfies Record<BaseFilters, unknown>;

/**
 * Hook que recibe la sección y devuelve los filtros correspondientes
 * @param section La sección para la cual se desean los filtros
 * @returns Un objeto con los valores predeterminados de los filtros
 */
export default function useFilters(section) {
  const defaultValues = sectionFilters[section];
  if (!defaultValues) {
    throw new Error(`Unknown section "${section}"`);
  }
  return useBaseFilters(defaultValues);
}

/**
 * Hook que recoge los valores de los filtros por defecto, los compara con los de la URL y los actualiza si cambian
 * @param defaultValues Los valores predeterminados de los filtros
 * @returns Un objeto con el estado de los filtros actuales y una función para cambiar los filtros y actualizar la URL
 */
function useBaseFilters(t0) {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultValues = t1;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  let state;
  if ($[2] !== defaultValues || $[3] !== searchParams) {
    state = {
      ...defaultValues
    };
    for (const [key, defaultValue] of Object.entries(defaultValues)) {
      if (Array.isArray(defaultValue)) {
        const paramValues = searchParams.getAll(key);
        if (paramValues.length > 0) {
          state[key] = convertArrayIfPossible(paramValues);
        } else {
          state[key] = defaultValue;
        }
      } else {
        if (typeof defaultValue === "number") {
          const paramValue = searchParams.get(key);
          state[key] = paramValue !== null ? Number(paramValue) : defaultValue;
        } else {
          if (typeof defaultValue === "boolean") {
            const paramValue_0 = searchParams.get(key);
            state[key] = paramValue_0 !== null ? paramValue_0 === "true" : defaultValue;
          } else {
            if (defaultValue === undefined) {
              const paramValue_1 = searchParams.get(key);
              if (paramValue_1 !== null) {
                state[key] = convertStringToTypedValue(paramValue_1);
              } else {
                state[key] = undefined;
              }
            } else {
              const paramValue_2 = searchParams.get(key);
              if (paramValue_2 !== null) {
                state[key] = convertStringToTypedValue(paramValue_2);
              } else {
                state[key] = defaultValue;
              }
            }
          }
        }
      }
    }
    searchParams.forEach((value, key_0) => {
      if (!(key_0 in state)) {
        const allValues = searchParams.getAll(key_0);
        if (allValues.length > 1) {
          state[key_0] = convertArrayIfPossible(allValues);
        } else {
          state[key_0] = convertStringToTypedValue(value);
        }
      }
    });
    $[2] = defaultValues;
    $[3] = searchParams;
    $[4] = state;
  } else {
    state = $[4];
  }
  const filtersState = state as T;
  let t2;
  if ($[5] !== defaultValues || $[6] !== filtersState || $[7] !== pathname || $[8] !== router || $[9] !== searchParams) {
    t2 = (filters, options) => {
      const params = new URLSearchParams(searchParams);
      const hasChanges = Object.entries(filters).some(t3 => {
        const [key_1, value_0] = t3;
        const currentValue = filtersState[key_1 as keyof T];
        if (Array.isArray(value_0) && Array.isArray(currentValue)) {
          if (value_0.length !== currentValue.length) {
            return true;
          }
          return !value_0.every(item_0 => currentValue.includes(item_0 as never)) || !currentValue.every(item => value_0.includes(item));
        }
        return value_0 !== currentValue;
      });
      if (!hasChanges) {
        return;
      }
      Object.entries(filters).forEach(t4 => {
        const [key_2, value_1] = t4;
        params.delete(key_2);
        if (value_1 === null || value_1 === undefined || value_1 === "") {
          return;
        }
        const defaultValue_0 = defaultValues[key_2 as keyof typeof defaultValues];
        const isDefaultValue = (() => {
          if (Array.isArray(value_1) && Array.isArray(defaultValue_0)) {
            if (value_1.length !== defaultValue_0.length) {
              return false;
            }
            return value_1.every(item_2 => defaultValue_0.includes(item_2 as never)) && defaultValue_0.every(item_1 => value_1.includes(item_1));
          }
          return value_1 === defaultValue_0;
        })();
        if (isDefaultValue) {
          return;
        }
        if (Array.isArray(value_1)) {
          value_1.forEach(item_3 => {
            if (item_3 !== null && item_3 !== "") {
              params.append(key_2, item_3.toString());
            }
          });
        } else {
          params.set(key_2, value_1.toString());
        }
      });
      const url = `${pathname}?${params.toString()}`;
      if (options?.replace) {
        router.replace(url, {
          scroll: false
        });
      } else {
        router.push(url, {
          scroll: false
        });
      }
    };
    $[5] = defaultValues;
    $[6] = filtersState;
    $[7] = pathname;
    $[8] = router;
    $[9] = searchParams;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const setFilters = t2;
  const t3 = filtersState as T;
  let t4;
  if ($[11] !== setFilters || $[12] !== t3) {
    t4 = [t3, setFilters];
    $[11] = setFilters;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4 as const;
}

// ----------------------------------------------------

/**
 * Función de utilidad para convertir cadenas a tipos apropiados
 * @param value
 * @returns
 */
const convertStringToTypedValue = (value: string): string | number | boolean => {
  // Comprobar si el valor es un booleano
  if (value === "true") return true;
  if (value === "false") return false;

  // Comprobar si el valor es un número
  const numberValue = Number(value);
  if (!isNaN(numberValue) && /^-?\d+(\.\d+)?$/.test(value)) {
    return numberValue;
  }

  // Devolver como cadena para cualquier otro caso
  return value;
};

/**
 * Convierte un array de cadenas a un array de números o booleanos si es posible
 *  @param arr Array de cadenas a convertir
 */
const convertArrayIfPossible = (arr: string[]): string[] | number[] | boolean[] => {
  if (arr.length === 0) return arr;

  // Comprobar si todos los valores pueden convertirse a números
  const allNumbers = arr.every(item => !isNaN(Number(item)) && /^-?\d+(\.\d+)?$/.test(item));
  if (allNumbers) {
    return arr.map(item => Number(item));
  }

  // Comprobar si todos los valores son cadenas booleanas
  const allBooleans = arr.every(item => item === "true" || item === "false");
  if (allBooleans) {
    return arr.map(item => item === "true");
  }

  // Devolver el array original de cadenas si es mixto o no convertible
  return arr;
};